import React, { useState } from "react";
import { Paper, Group, Text, ActionIcon, Tabs, ScrollArea, Code, Button, JsonInput } from "@mantine/core";
import { MdClose, MdContentCopy } from "react-icons/md";
import { useUserRoles } from "../../../hooks";

interface DeveloperToolsProps {
    sqlQuery?: string;
    chartData?: any;
    onClose: () => void;
}

/**
 * Developer Tools Component
 *
 * Provides debugging capabilities for charts including:
 * - SQL Query display and copy functionality
 * - Chart data JSON export and download
 *
 * This component is self-contained and will only render for administrators.
 */
export const DeveloperTools: React.FC<DeveloperToolsProps> = ({ sqlQuery, chartData, onClose }) => {
    const { isAdmin } = useUserRoles();
    const [activeTab, setActiveTab] = useState<string | null>("sql");

    if (!isAdmin) {
        return null;
    }

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error("Failed to copy to clipboard:", err);
        }
    };

    const formattedJsonData = chartData ? JSON.stringify(chartData, null, 2) : "";

    return (
        <Paper p="md" withBorder radius="md" style={{ backgroundColor: "#f8f9fa" }}>
            <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                    Developer Tools
                </Text>
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={onClose} title="Close developer tools">
                    <MdClose size={16} />
                </ActionIcon>
            </Group>

            <Tabs value={activeTab} onChange={setActiveTab}>
                <Tabs.List>
                    <Tabs.Tab value="sql">SQL Query</Tabs.Tab>
                    <Tabs.Tab value="json">Chart Data</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="sql" pt="md">
                    {sqlQuery ? (
                        <>
                            <Group justify="flex-end" mb="xs">
                                <Button size="xs" variant="light" leftSection={<MdContentCopy size={14} />} onClick={() => copyToClipboard(sqlQuery)}>
                                    Copy SQL
                                </Button>
                            </Group>
                            <ScrollArea.Autosize mah={200}>
                                <Code block style={{ fontSize: "12px", whiteSpace: "pre-wrap" }}>
                                    {sqlQuery}
                                </Code>
                            </ScrollArea.Autosize>
                        </>
                    ) : (
                        <Text size="sm" c="dimmed">
                            No SQL query available
                        </Text>
                    )}
                </Tabs.Panel>

                <Tabs.Panel value="json" pt="md">
                    {chartData ? (
                        <>
                            <Group justify="flex-end" mb="xs">
                                <Button size="xs" variant="light" leftSection={<MdContentCopy size={14} />} onClick={() => copyToClipboard(formattedJsonData)}>
                                    Copy JSON
                                </Button>
                            </Group>
                            <JsonInput
                                value={formattedJsonData}
                                readOnly
                                autosize
                                minRows={8}
                                maxRows={15}
                                styles={{
                                    input: {
                                        fontSize: "12px",
                                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid #dee2e6"
                                    }
                                }}
                            />
                        </>
                    ) : (
                        <Text size="sm" c="dimmed">
                            No chart data available
                        </Text>
                    )}
                </Tabs.Panel>
            </Tabs>
        </Paper>
    );
};
